import { computed } from '@ember/object';
import { equal } from '@ember/object/computed';
import { isEmpty } from '@ember/utils';
import { isArray } from '@ember/array';
import InclusionType from '@square/dashboard-data/models/squareup/api/items/fee/inclusion-type';
import Enum from '@square/dashboard-utilities/utils/enum';
// eslint-disable-next-line ember/no-mixins
import DisplayNameMixin from '@square/dashboard-data/mixins/display-name-mixin';
import ModifierList from '@square/dashboard-data/models/squareup/beemo/api/v2/models/adjustment/modifier-list';
import ModifierQuantities from '@square/dashboard-data/models/squareup/beemo/api/v2/models/modifier-quantities';
import MoneyProto from '@square/dashboard-data/models/squareup/common/money-proto';
import NameOrTranslationType from '@square/dashboard-data/models/squareup/beemo/translation-types/name-or-translation-type';
import { getFormat } from '@square/dashboard-utilities/utils/i18n-deprecated/format';
import ConversationalMode from '@square/dashboard-data/models/squareup/beemo/api/v2/models/conversational-mode';
import ComboDetails from '@square/dashboard-data/models/squareup/beemo/api/v2/models/combo-details';
import { ProtoBaseModel, attr, repeated } from '@square/dashboard-data/utils/ember-model-stubs';
export const AdjustmentType = new Enum('TAX',
// An Additive tax.
'UNITEMIZED_TAX',
// An Additive tax.
'INCLUSIVE_TAX',
// An Inclusive tax.
'DISCOUNT', 'REWARD', 'COUPON', 'ITEM_MODIFIER', 'COMP', 'SURCHARGE', 'COMBO');
export const DiscountApplicationScope = new Enum('UNKNOWN', 'ITEMIZATION_LEVEL', 'CART_LEVEL');

// This should live in features.js but from my understanding we can't access that
// from here, and to have it reside there means it will need to be passed through multiple modules
// which is rather painful. We don't foresee this changing so this will live here for now. I know.
// Sorry. :(
const TAX_LABEL_START = 'A'.codePointAt(0);
const MAX_DECIMAL_PRECISION_OF_FEE_PERCENTAGE = 5;
const BaseAdjustment = ProtoBaseModel.extend(DisplayNameMixin, {
  type: attr(AdjustmentType),
  adjustmentGroup: attr(String),
  nameOrTranslationType: attr(NameOrTranslationType),
  appliedMoney: attr(MoneyProto),
  appliedToMoney: attr(MoneyProto),
  afterApplicationTotalMoney: attr(MoneyProto),
  taxLabelOrder: attr(Number),
  // Only populated for surcharges
  surchargeType: attr(String),
  rate: attr(Number),
  inclusionType: attr(InclusionType),
  // otherwise known as a modifier set
  modifierList: attr(ModifierList),
  modifierQuantities: attr(ModifierQuantities),
  applicationScope: attr(DiscountApplicationScope),
  conversationalModes: repeated(ConversationalMode),
  nameWithConversationalModes: computed('name', 'conversationalModes.@each.name', function () {
    if (isArray(this.conversationalModes)) {
      return [...this.conversationalModes.mapBy('name'), this.name].join(' ');
    } else {
      return this.name;
    }
  }),
  quantity: computed('modifierQuantities.{modiferQuantity,modifierQuantity}', function () {
    const quantity = Number(this.modifierQuantities?.modifierQuantity);
    if (quantity > 1) {
      return `×${this.modifierQuantities.modifierQuantity}`;
    }
    return null;
  }),
  isComp: equal('type', AdjustmentType.COMP),
  ratePercentage: computed('rate', function () {
    // rate may be 0 but that is valid
    if (isEmpty(this.rate)) {
      return undefined;
    }
    return getFormat().percent(this.rate * 100, {
      maximumFractionDigits: MAX_DECIMAL_PRECISION_OF_FEE_PERCENTAGE
    });
  }),
  taxLabel: computed('taxLabelOrder', function () {
    return String.fromCodePoint(TAX_LABEL_START + this.taxLabelOrder);
  })
});
export default BaseAdjustment.extend({
  comboDetails: attr(ComboDetails),
  // Only populated for (taxable) surcharges
  tax: repeated(BaseAdjustment)
});